import api from "./api";

const PUSH_BASE_PATHS = ["/api/notifications", "/api/v1/notifications"];

let pushRoutesUnavailable = false;
let pushBackendNotConfigured = false;

async function requestWithFallback(method, pathSuffix, payload = null) {
  let lastError = null;

  for (const basePath of PUSH_BASE_PATHS) {
    try {
      const url = `${basePath}${pathSuffix}`;
      if (method === "get") {
        return await api.get(url);
      }
      if (method === "post") {
        return await api.post(url, payload);
      }
      throw new Error(`Unsupported request method: ${method}`);
    } catch (error) {
      const status = error?.response?.status;
      lastError = error;

      // Try next candidate path for 404 only.
      if (status === 404) {
        continue;
      }

      throw error;
    }
  }

  throw lastError;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

export async function getUnreadNotificationCount() {
  const response = await api.get("/api/notifications/unread/count");
  return Number(response?.data?.data?.count || 0);
}

export async function getNotificationHistory(limit = 10, skip = 0) {
  const response = await api.get("/api/notifications", {
    params: { limit, skip },
  });

  return response?.data?.data?.notifications || [];
}

export async function markNotificationAsRead(notificationId) {
  const response = await api.patch(`/api/notifications/${notificationId}/read`);
  return response?.data?.data || null;
}

export async function markAllNotificationsAsRead() {
  const response = await api.patch("/api/notifications/read-all");
  return response?.data?.data || null;
}

export async function getPushPublicKey() {
  const response = await requestWithFallback("get", "/push/public-key");
  return String(response?.data?.data?.publicKey || "");
}

export async function checkPushBackendAvailability() {
  if (pushRoutesUnavailable || pushBackendNotConfigured) {
    return false;
  }

  try {
    const publicKey = await getPushPublicKey();
    const available = Boolean(publicKey);
    if (!available) {
      pushRoutesUnavailable = true;
    }
    return available;
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404) {
      pushRoutesUnavailable = true;
      return false;
    }
    if (status === 503) {
      pushBackendNotConfigured = true;
      return false;
    }
    return false;
  }
}

export async function subscribePushSubscription(subscription, deviceInfo = {}) {
  const payload =
    typeof subscription.toJSON === "function"
      ? subscription.toJSON()
      : subscription;

  const response = await requestWithFallback("post", "/push/subscribe", {
    subscription: payload,
    deviceInfo,
  });

  return response.data;
}

export async function registerPushInBackground({
  requestPermission = false,
  forceResubscribe = false,
} = {}) {
  console.debug("[Push] registerPushInBackground: called", {
    requestPermission,
    forceResubscribe,
  });
  if (pushRoutesUnavailable) {
    console.warn("[Push] Backend route missing");
    return { ok: false, reason: "backend-route-missing" };
  }

  if (pushBackendNotConfigured) {
    console.warn("[Push] Backend not configured");
    return { ok: false, reason: "backend-not-configured" };
  }

  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    console.error("[Push] Service worker or PushManager not supported");
    return { ok: false, reason: "unsupported" };
  }

  let permission = Notification.permission;
  console.debug("[Push] Notification.permission:", permission);
  if (permission === "default" && requestPermission) {
    console.debug("[Push] Requesting notification permission...");
    permission = await Notification.requestPermission();
    console.debug("[Push] Notification.permission after request:", permission);
  }

  if (permission !== "granted") {
    console.warn("[Push] Notification permission not granted:", permission);
    return {
      ok: false,
      reason: permission === "denied" ? "denied" : "not-granted",
    };
  }

  console.debug("[Push] Waiting for service worker ready...");
  const registration = await navigator.serviceWorker.ready;
  console.debug("[Push] Service worker ready:", registration);
  let subscription = await registration.pushManager.getSubscription();
  console.debug("[Push] Existing subscription:", subscription);

  try {
    console.debug("[Push] Fetching public VAPID key...");
    const publicKey = await getPushPublicKey();
    console.debug("[Push] Public VAPID key:", publicKey);
    if (!publicKey) {
      console.error("[Push] Missing public VAPID key");
      return { ok: false, reason: "missing-public-key" };
    }

    const applicationServerKey = urlBase64ToUint8Array(publicKey);
    console.debug("[Push] Application server key:", applicationServerKey);

    if (subscription && forceResubscribe) {
      console.debug("[Push] Unsubscribing existing subscription...");
      await subscription.unsubscribe();
      subscription = null;
      console.debug("[Push] Existing subscription removed");
    }

    if (!subscription) {
      console.debug("[Push] Subscribing to pushManager...");
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
      console.debug("[Push] New subscription:", subscription);
    }

    console.debug("[Push] Sending subscription to backend...");
    await subscribePushSubscription(subscription, {
      userAgent: navigator.userAgent,
      platform: navigator.platform || "",
      browser: navigator.appName || "",
    });
    console.debug("[Push] Subscription sent to backend");

    return { ok: true, reason: "subscribed" };
  } catch (error) {
    const status = error?.response?.status;
    console.error("[Push] Error during push registration:", error);

    if (status === 404) {
      pushRoutesUnavailable = true;
      return { ok: false, reason: "backend-route-missing" };
    }

    if (status === 503) {
      pushBackendNotConfigured = true;
      return { ok: false, reason: "backend-not-configured" };
    }

    return {
      ok: false,
      reason: "subscription-failed",
      error,
    };
  }
}

export async function requestNotificationPermission() {
  if (!("Notification" in window)) {
    return "unsupported";
  }

  if (Notification.permission === "granted") {
    return "granted";
  }

  return Notification.requestPermission();
}

export async function subscribeToPushNotifications(options = {}) {
  console.debug("[Push] subscribeToPushNotifications: called", options);
  const result = await registerPushInBackground({
    requestPermission: options.requestPermission !== false,
    forceResubscribe: Boolean(options.forceResubscribe),
  });
  console.debug("[Push] registerPushInBackground result:", result);

  if (!result.ok) {
    console.error("[Push] subscribeToPushNotifications failed:", result);
    throw new Error(`Push subscription failed: ${result.reason}`);
  }

  const registration = await navigator.serviceWorker.ready;
  console.debug("[Push] Service worker ready:", registration);
  const subscription = await registration.pushManager.getSubscription();
  console.debug("[Push] Subscription after register:", subscription);

  return subscription;
}

export async function unsubscribeFromPushNotifications() {
  console.debug("[Push] unsubscribeFromPushNotifications: called");
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    console.error("[Push] unsubscribeFromPushNotifications: unsupported");
    return { ok: false, reason: "unsupported" };
  }

  const registration = await navigator.serviceWorker.ready;
  console.debug("[Push] Service worker ready:", registration);
  const subscription = await registration.pushManager.getSubscription();
  console.debug("[Push] Subscription before unsubscribe:", subscription);

  if (!subscription) {
    console.debug("[Push] No subscription to unsubscribe");
    return { ok: true, reason: "not-subscribed" };
  }

  const endpoint = subscription.endpoint;
  const unsubscribedInBrowser = await subscription.unsubscribe();
  console.debug("[Push] Unsubscribed in browser:", unsubscribedInBrowser);

  try {
    await requestWithFallback("post", "/push/unsubscribe", { endpoint });
    console.debug("[Push] Backend unsubscribe request sent");
  } catch (error) {
    // If backend endpoint is missing or unavailable, browser-side unsubscribe is still valid.
    const status = error?.response?.status;
    if (status !== 404 && status !== 503) {
      console.error("[Push] Backend unsubscribe error:", error);
      throw error;
    }
    console.warn(
      "[Push] Backend unsubscribe endpoint missing or unavailable",
      error,
    );
  }

  return { ok: unsubscribedInBrowser, reason: "unsubscribed" };
}

export async function getPushSubscriptionStatus() {
  const supported = "serviceWorker" in navigator && "PushManager" in window;
  const permission =
    "Notification" in window ? Notification.permission : "unsupported";

  if (!supported) {
    return {
      supported: false,
      subscribed: false,
      permission,
      subscription: null,
    };
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  return {
    supported: true,
    subscribed: Boolean(subscription),
    permission,
    subscription,
  };
}

export async function showLocalNotification(title, options = {}) {
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service Worker is not supported in this browser");
  }

  if (!("Notification" in window) || Notification.permission !== "granted") {
    throw new Error("Notification permission is not granted");
  }

  const registration = await navigator.serviceWorker.ready;

  return registration.showNotification(title, {
    body: options.body || "",
    icon: options.icon || "/image.png",
    badge: options.badge || "/image.png",
    tag: options.tag || "local-notification",
    requireInteraction: Boolean(options.requireInteraction),
    data: options.data || {},
  });
}

export function setupPushMessageListener(callback) {
  if (!("serviceWorker" in navigator) || typeof callback !== "function") {
    return () => {};
  }

  const handler = (event) => {
    if (event?.data?.type === "PUSH_NOTIFICATION_RECEIVED") {
      callback(event.data.payload);
    }
  };

  navigator.serviceWorker.addEventListener("message", handler);

  return () => {
    navigator.serviceWorker.removeEventListener("message", handler);
  };
}
